import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Plus, Edit, Trash2, Save, Tag } from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';

export default function PromoCodesTab({ promoCodes, fetchData }) {
  const [showPromoForm, setShowPromoForm] = useState(false);
  const [editingPromo, setEditingPromo] = useState(null);
  const [promoForm, setPromoForm] = useState({
    code: '',
    discount_type: 'percentage',
    discount_value: '',
    min_order_amount: '',
    max_discount: '',
    usage_limit: '100',
    is_active: true,
    valid_until: ''
  });

  const handlePromoSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        code: promoForm.code.toUpperCase(),
        discount_type: promoForm.discount_type,
        discount_value: parseFloat(promoForm.discount_value),
        min_order_amount: promoForm.min_order_amount ? parseFloat(promoForm.min_order_amount) : 0,
        max_discount: promoForm.max_discount ? parseFloat(promoForm.max_discount) : null,
        usage_limit: parseInt(promoForm.usage_limit) || 100,
        is_active: promoForm.is_active,
        valid_until: promoForm.valid_until || null
      };

      if (editingPromo) {
        await api.put(`/promo-codes/${editingPromo.id}`, data);
        toast.success('Promo code updated!');
      } else {
        await api.post('/promo-codes', data);
        toast.success('Promo code created!');
      }
      setShowPromoForm(false);
      setEditingPromo(null);
      resetPromoForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save promo code');
    }
  };

  const resetPromoForm = () => {
    setPromoForm({
      code: '',
      discount_type: 'percentage',
      discount_value: '',
      min_order_amount: '',
      max_discount: '',
      usage_limit: '100',
      is_active: true,
      valid_until: ''
    });
  };

  const deletePromoCode = async (promoId) => {
    if (!window.confirm('Are you sure you want to delete this promo code?')) return;
    try {
      await api.delete(`/promo-codes/${promoId}`);
      toast.success('Promo code deleted!');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete promo code');
    }
  };

  return (
    <div data-testid="promo-codes-tab">
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-serif text-2xl font-semibold text-[#1a3a5c]">Promo Codes</h2>
        <button
          onClick={() => {
            setShowPromoForm(true);
            setEditingPromo(null);
            resetPromoForm();
          }}
          className="bg-[#1a3a5c] text-white px-6 py-3 rounded-sm hover:bg-[#1f4268] transition-colors flex items-center space-x-2"
          data-testid="add-promo-button"
        >
          <Plus className="h-5 w-5" />
          <span>Add Promo Code</span>
        </button>
      </div>

      {showPromoForm && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white p-6 rounded-lg border border-gray-200 mb-6"
        >
          <h3 className="font-serif text-xl font-semibold text-[#1a3a5c] mb-4">
            {editingPromo ? 'Edit Promo Code' : 'Create Promo Code'}
          </h3>
          <form onSubmit={handlePromoSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Promo Code</label>
              <input
                type="text"
                value={promoForm.code}
                onChange={(e) => setPromoForm({ ...promoForm, code: e.target.value.toUpperCase() })}
                className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c] uppercase"
                placeholder="e.g., SAVE20"
                required
                data-testid="promo-code-input"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Discount Type</label>
              <select
                value={promoForm.discount_type}
                onChange={(e) => setPromoForm({ ...promoForm, discount_type: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]"
                data-testid="promo-type-select"
              >
                <option value="percentage">Percentage (%)</option>
                <option value="fixed">Fixed Amount (₹)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Discount Value {promoForm.discount_type === 'percentage' ? '(%)' : '(₹)'}
              </label>
              <input
                type="number"
                step="0.01"
                value={promoForm.discount_value}
                onChange={(e) => setPromoForm({ ...promoForm, discount_value: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]"
                placeholder={promoForm.discount_type === 'percentage' ? 'e.g., 10' : 'e.g., 500'}
                required
                data-testid="promo-value-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Min Order Amount (₹)</label>
              <input
                type="number"
                value={promoForm.min_order_amount}
                onChange={(e) => setPromoForm({ ...promoForm, min_order_amount: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]"
                placeholder="0"
                data-testid="promo-min-order-input"
              />
            </div>

            {promoForm.discount_type === 'percentage' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Max Discount (₹) - Optional</label>
                <input
                  type="number"
                  value={promoForm.max_discount}
                  onChange={(e) => setPromoForm({ ...promoForm, max_discount: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]"
                  placeholder="e.g., 1000"
                  data-testid="promo-max-discount-input"
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Usage Limit</label>
              <input
                type="number"
                value={promoForm.usage_limit}
                onChange={(e) => setPromoForm({ ...promoForm, usage_limit: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]"
                placeholder="100"
                data-testid="promo-usage-limit-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Valid Until (Optional)</label>
              <input
                type="datetime-local"
                value={promoForm.valid_until}
                onChange={(e) => setPromoForm({ ...promoForm, valid_until: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]"
                data-testid="promo-valid-until-input"
              />
            </div>

            <div className="flex items-center">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={promoForm.is_active}
                  onChange={(e) => setPromoForm({ ...promoForm, is_active: e.target.checked })}
                  className="rounded border-gray-300 text-[#1a3a5c] focus:ring-[#1a3a5c]"
                  data-testid="promo-active-checkbox"
                />
                <span className="text-sm font-medium text-gray-700">Active</span>
              </label>
            </div>

            <div className="md:col-span-2 flex justify-end space-x-4">
              <button
                type="button"
                onClick={() => {
                  setShowPromoForm(false);
                  setEditingPromo(null);
                }}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-sm hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="bg-[#1a3a5c] text-white px-6 py-2 rounded-sm hover:bg-[#1f4268] flex items-center space-x-2"
                data-testid="save-promo-button"
              >
                <Save className="h-4 w-4" />
                <span>{editingPromo ? 'Update' : 'Create'}</span>
              </button>
            </div>
          </form>
        </motion.div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {promoCodes.map((promo) => (
          <div key={promo.id} className={`bg-white p-4 rounded-lg border ${promo.is_active ? 'border-green-200' : 'border-gray-200'}`}>
            <div className="flex justify-between items-start mb-3">
              <div className="flex items-center space-x-2">
                <Tag className="h-5 w-5 text-[#C9A967]" />
                <span className="font-mono text-lg font-bold text-[#1a3a5c]">{promo.code}</span>
              </div>
              <span className={`px-2 py-1 text-xs rounded-full ${promo.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                {promo.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
            
            <p className="text-2xl font-bold text-[#1a3a5c] mb-2">
              {promo.discount_type === 'percentage' ? `${promo.discount_value}% OFF` : `₹${promo.discount_value} OFF`}
            </p>
            
            <div className="text-sm text-gray-600 space-y-1 mb-4">
              <p>Min Order: ₹{promo.min_order_amount || 0}</p>
              {promo.max_discount && <p>Max Discount: ₹{promo.max_discount}</p>}
              <p>Used: {promo.used_count || 0} / {promo.usage_limit}</p>
              {promo.valid_until && <p>Expires: {new Date(promo.valid_until).toLocaleDateString()}</p>}
            </div>

            <div className="flex space-x-2">
              <button
                onClick={() => {
                  setEditingPromo(promo);
                  setPromoForm({
                    code: promo.code,
                    discount_type: promo.discount_type,
                    discount_value: promo.discount_value.toString(),
                    min_order_amount: (promo.min_order_amount || '').toString(),
                    max_discount: (promo.max_discount || '').toString(),
                    usage_limit: promo.usage_limit.toString(),
                    is_active: promo.is_active,
                    valid_until: promo.valid_until ? promo.valid_until.slice(0, 16) : ''
                  });
                  setShowPromoForm(true);
                }}
                className="flex-1 bg-blue-600 text-white px-3 py-2 rounded-sm text-sm hover:bg-blue-700"
              >
                <Edit className="h-4 w-4 inline mr-1" /> Edit
              </button>
              <button
                onClick={() => deletePromoCode(promo.id)}
                className="flex-1 bg-red-600 text-white px-3 py-2 rounded-sm text-sm hover:bg-red-700"
              >
                <Trash2 className="h-4 w-4 inline mr-1" /> Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {promoCodes.length === 0 && (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <Tag className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No promo codes yet. Create your first promo code!</p>
        </div>
      )}
    </div>
  );
}
